import { get, sortBy } from 'lodash';

const state = {
	mainProfile: {
		contactId: null,
		userId: 0,
		firstName: '',
		lastName: '',
		middleName: '',
		preferredName: '',
		phoneNumber: '',
		phoneExtension: '',
		mobilePhoneNumber: '',
		email: '',
		websiteUrl: '',
		nmlsNumber: '',
		addresses: [],
		doingBusinessAs: '',
		profilePhoto: {
			imageFormat: '',
			imageUri: '',
			referenceId: '',
		},
		socialMediaDetails: [
			{
				type: 'FaceBook',
				value: '',
			},
			{
				type: 'Twitter',
				value: '',
			},
			{
				type: 'Instagram',
				value: '',
			},
			{
				type: 'LinkedIn',
				value: '',
			},
			{
				type: 'TikTok',
				value: '',
			},
		],
		licenses: [
			{
				licenseKey: '',
				licenseValue: '',
				expirationDate: '',
			},
		],
		brand: [
			{
				name: 'HeaderFontFamily',
				value: 'Arial',
			},
			{
				name: 'Color1',
				value: '',
			},
			{
				name: 'Color2',
				value: '',
			},
			{
				name: 'Color3',
				value: '',
			},
		],
		organizationId: [0],
		activeIndicator: false,
		isFirstTimeSetupCompleted: false,
		termsOfUse: true,
		complianceLogo: {
			imageFormat: '',
			imageUri: '',
			referenceId: '',
		},
		personalCompanyLogo: {
			imageFormat: '',
			imageUri: '',
			referenceId: '',
		},
		isAccountExecutive: false,
		roles: [],
		loginEmail: '',
		associatedBrokerShops: [
			{
				organizationId: 7512,
				brokerId: 'UWM01B',
				brokerName: 'ITBroker Company Test Account',
				rolodexEntityId: 59660,
			},
		],
	},
	impersonatedUser: 0,
	isHyrdated: false,
	brokerDetails: [],
	profileSelector: {
		brokerShopDetails: [],
		isInternalUser: false,
		canPerformProfileSelector: false,
		profileLogo: {
			imageFormat: '',
			imageUri: '',
			referenceId: '',
		},
		userCompanyName: '',
		userPreferredName: '',
		userEmailAddress: '',
		loginEmail: '',
		showPermissionsTab: true,
	},
	mainProfileSelector: {
		brokerShopDetails: [],
		isInternalUser: false,
		canPerformProfileSelector: false,
		profileLogo: {
			imageFormat: '',
			imageUri: '',
			referenceId: '',
		},
		userCompanyName: '',
		userPreferredName: '',
		userEmailAddress: '',
		loginEmail: '',
		showPermissionsTab: true,
	},
	owners: [],
};

const mutations = {
	SET_MAIN_USER(state, profileInfo) {
		state.mainProfile = profileInfo;
		state.isHyrdated = true;
	},
	SET_IMPERSONATED_USER(state, userId) {
		state.impersonatedUser = userId;
	},
	SET_BROKER_DETAILS(state, payload) {
		state.brokerDetails = payload;
	},
	SET_AE_BROKER_DETAILS(state, brokerDetails) {
		state.brokerDetails = [];
		state.brokerDetails.push(brokerDetails);
	},
	SET_PROFILE_SELECTOR(state, profileSelectorData) {
		state.profileSelector = profileSelectorData;
	},
	SET_MAIN_PROFILE_SELECTOR(state, profileSelectorData) {
		state.mainProfileSelector = profileSelectorData;
	},
	SET_ALL_OWNERS_BY_ENTITY(state, owners) {
		state.owners = [];
		owners = owners.filter((item) => item.contactId !== 0);
		state.owners = [...state.owners, ...owners];
	},
};

const actions = {
	setMainUser({ commit }, profileInfo) {
		// some sort of api call to grab the associated brokers? or maybe grab it from existing user state
		commit('SET_MAIN_USER', profileInfo);
	},
	setImpersonatedUser({ commit }, userId) {
		commit('SET_IMPERSONATED_USER', userId);
	},
	setBrokerShopDetails({ commit }, payload) {
		commit('SET_BROKER_DETAILS', payload);
	},
	setAeBrokerDetails({ commit }, brokerDetails) {
		commit('SET_AE_BROKER_DETAILS', brokerDetails);
	},
	setProfileSelector({ commit }, profileSelectorData) {
		commit('SET_PROFILE_SELECTOR', profileSelectorData);
	},
	setMainBrokerShopDetails({ commit }, profileSelectorData) {
		commit('SET_MAIN_PROFILE_SELECTOR', profileSelectorData);
	},
	setAllOwnersByEntity({ commit }, owners) {
		commit('SET_ALL_OWNERS_BY_ENTITY', owners);
	},
};

const getters = {
	isInternalUser(state) {
		return () => {
			return state.mainProfileSelector.isInternalUser;
		};
	},
	currentUserRolodexIds: (state) => {
		let entityId = null;
		let getAllContactsIds = [];

		if (
			state.mainProfileSelector.isInternalUser &&
			state.brokerDetails.length > 0
		) {
			entityId = state.brokerDetails[0].shopDetail.entityId;
			getAllContactsIds = state.brokerDetails[0].users.map(
				(user) => user.rolodexContactId,
			);
		} else if (state.profileSelector.brokerShopDetails.length > 0) {
			entityId =
				state.profileSelector.brokerShopDetails[0].shopDetail.rolodexEntityId;
			getAllContactsIds = state.profileSelector.brokerShopDetails[0].users.map(
				(user) => user.rolodexContactId,
			);
		}

		const contactsIds = state.profileSelector.canPerformProfileSelector
			? getAllContactsIds
			: [state.profileSelector.rolodexContactId];
		return { entityId, contactsIds };
	},
	currentImpersonatedUserRolodexId: (state) => {
		let entityId = null;
		if (
			state.mainProfileSelector.isInternalUser &&
			state.brokerDetails.length > 0
		) {
			entityId = state.brokerDetails[0].shopDetail.entityId;
		} else if (state.profileSelector.brokerShopDetails.length > 0) {
			entityId =
				state.profileSelector.brokerShopDetails[0].shopDetail.rolodexEntityId;
		}
		const contactsIds = [state.profileSelector.rolodexContactId];
		return { entityId, contactsIds };
	},
	currentUserProfile: (state) => {
		const entityId = get(
			state,
			'mainProfileSelector.brokerShopDetails[0].shopDetail.rolodexEntityId',
		);
		const contactId = state.mainProfile.rolodexContactId;
		const userId = state.mainProfile.userId;
		const firstName = state.mainProfile.firstName;
		const lastName = state.mainProfile.lastName;
		const middleName = state.mainProfile.middleName;
		const preferredName = state.mainProfile.preferredName;
		return {
			entityId,
			contactId,
			userId,
			firstName,
			lastName,
			middleName,
			preferredName,
		};
	},
	sortedBrokerShopUsers: (state) => {
		let users = [];
		if (state.mainProfileSelector.isInternalUser) {
			users = [...state.brokerDetails[0].users];
		} else {
			users = [...state.mainProfileSelector.brokerShopDetails[0].users];
		}

		if (
			state.owners.length > 0 &&
			state.mainProfileSelector.canPerformProfileSelector
		) {
			const owners = state.owners;
			return sortBy(
				owners,
				(owner) => owner.lastName && owner.lastName.toLowerCase(),
			);
		}

		return sortBy(
			users,
			(user) => user.lastName && user.lastName.toLowerCase(),
		);
	},
	brokerContactsList: (state) => {
		let users = [];
		if (state.mainProfileSelector.isInternalUser) {
			users = state.brokerDetails[0].users || null;
		} else {
			users = state.mainProfileSelector.brokerShopDetails[0].users;
		}

		if (
			state.owners.length > 0 &&
			state.mainProfileSelector.canPerformProfileSelector
		) {
			return state.owners;
		}

		return users;
	},
	canImpersonate: (state) => {
		return get(state, 'mainProfileSelector.canPerformProfileSelector');
	},
};

export default {
	state,
	actions,
	mutations,
	getters,
};
